/**
 * The one thing that sits between input hardware and the game.
 *
 * Producers — keyboard+mouse, touch, later gamepad — write into this object. The player
 * controller only ever reads it, and never touches a DOM event. That seam is why touch is
 * a first-class input rather than a port, and why adding a gamepad later is a new file
 * instead of an edit to movement code. See CLAUDE.md, architectural rule 2.
 *
 * This is deliberately a plain mutable object rather than React state or a Zustand store:
 * it changes every frame, and per-frame data must never go through React.
 */

export type Vec2 = { x: number; y: number };

export type InputState = {
  /**
   * Desired movement, analog, in the range -1..1 on each axis and never longer than 1.
   * +y is forward, +x is right, both relative to where the camera is facing.
   */
  move: Vec2;
  /**
   * Look delta accumulated since the last frame. Consumed and zeroed each frame — it is a
   * delta, not a position, so applying it twice would double the rotation.
   */
  look: Vec2;
  /**
   * Whether the player is asking to move at a run. A level, not an edge: held for as long
   * as the gesture is held, cleared with it. Producers say what was asked for and never
   * how fast anything moves — the speed lives with the other movement numbers in
   * `engine/constants.ts` (design D1).
   */
  sprint: boolean;
  /** Edge-triggered: set by a producer, cleared by whoever handles it. */
  interact: boolean;
  /**
   * Whether the interact control is *being held*, as opposed to having been pressed. A
   * level, like `sprint`, and read by whatever acts for as long as the hold lasts —
   * dragging a table is the first. The same control therefore says two things: a press is
   * a moment (pick this up, open this door) and a hold is a duration. Nothing consumes
   * this; only the release clears it, or `resetInput` when the release never arrives.
   */
  interactHeld: boolean;
  jump: boolean;
  /** Edge-triggered: launch the carried object. Separate from interact on purpose. */
  throw: boolean;
  /** Edge-triggered: put the held item in the pocket (design D5). */
  stow: boolean;
  /**
   * Edge-triggered: take this slot's item back into the hands, or null when nothing is
   * pending. A number rather than three flags because the slot count is a rule, not a
   * binding — the keyboard's 1/2/3 and a tap on the HUD both arrive here as an index.
   */
  pocketSlot: number | null;
  /**
   * Edge-triggered: the player tapped or clicked *at a place* rather than at the
   * crosshair, in normalized device coordinates (-1..1, +y up). Null when nothing is
   * pending. This is what lets a thumb reach an object that is not dead centre; a press
   * with no position (the E key, a click under pointer lock) sets `interact` instead.
   */
  tap: Vec2 | null;
};

export function createInputState(): InputState {
  return {
    move: { x: 0, y: 0 },
    look: { x: 0, y: 0 },
    sprint: false,
    interact: false,
    interactHeld: false,
    jump: false,
    throw: false,
    stow: false,
    pocketSlot: null,
    tap: null,
  };
}

/**
 * The live input for the running game. A module singleton on purpose — producers and the
 * player controller need to reach it without prop-drilling through the React tree, and it
 * must be readable inside `useFrame` without causing a render.
 */
export const inputState: InputState = createInputState();

/** Read the pending look delta and clear it, so a frame applies it exactly once. */
export function consumeLook(state: InputState): Vec2 {
  const delta = { x: state.look.x, y: state.look.y };
  state.look.x = 0;
  state.look.y = 0;
  return delta;
}

/** Read and clear a pending interaction. */
export function consumeInteract(state: InputState): boolean {
  const pending = state.interact;
  state.interact = false;
  return pending;
}

/** Read and clear a pending throw. */
export function consumeThrow(state: InputState): boolean {
  const pending = state.throw;
  state.throw = false;
  return pending;
}

/** Read and clear a pending stow. */
export function consumeStow(state: InputState): boolean {
  const pending = state.stow;
  state.stow = false;
  return pending;
}

/** Read and clear a pending pocket-slot request. Slot zero is a slot, not an absence. */
export function consumePocketSlot(state: InputState): number | null {
  const pending = state.pocketSlot;
  state.pocketSlot = null;
  return pending;
}

/** Read and clear a pending tap, and where on the screen it landed. */
export function consumeTap(state: InputState): Vec2 | null {
  const pending = state.tap;
  state.tap = null;
  return pending;
}

/**
 * Zero everything, in place.
 *
 * Called when focus is lost, pointer lock ends, or a touch is cancelled — otherwise a key
 * held at the moment of blur never receives its keyup and the player walks into a wall
 * forever.
 */
export function resetInput(state: InputState): void {
  state.move.x = 0;
  state.move.y = 0;
  state.look.x = 0;
  state.look.y = 0;
  state.sprint = false;
  state.interact = false;
  state.interactHeld = false;
  state.jump = false;
  state.throw = false;
  state.stow = false;
  state.pocketSlot = null;
  state.tap = null;
}
